import { z } from "zod";
import { AutopilotOptions } from "./autopilot.js";
import { ErrorCode } from "./errors.js";
import { Operation } from "./operations.js";
import { Server } from "./servers.js";
import { Labels } from "./ssh-keys.js";
import { MAX_VOLUMES_PER_SERVER } from "./volumes.js";

/**
 * A server name as providers require it: a valid hostname (RFC 1123) —
 * dot-separated labels of letters, digits and inner hyphens, each at most 63
 * characters, 253 in total.
 */
export const SERVER_NAME_PATTERN =
  /^(?=.{1,253}$)[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

/** Servers created by one request at most; each becomes its own provider call. */
export const MAX_SERVERS_PER_CREATE = 10;

/** Cloud-init user data limit of the provider (Hetzner: 32 KiB). */
export const MAX_USER_DATA_BYTES = 32 * 1024;

/** Upper bounds of the request; the provider may allow fewer and then refuses itself. */
export const MAX_NETWORKS_PER_SERVER = 10;
export const MAX_FIREWALLS_PER_SERVER = 10;

export const byteLength = (text: string): number => new TextEncoder().encode(text).length;

/** The names a create request produces: the name itself, or `<name>-1` … `<name>-<count>`. */
export function serverNames(name: string, count: number): string[] {
  if (count <= 1) return [name];
  return Array.from({ length: count }, (_, i) => `${name}-${i + 1}`);
}

export const CreateServerRequest = z
  .object({
    /** With `count` > 1 the base of the names, which get `-1` … `-<count>`. */
    name: z.string().trim().regex(SERVER_NAME_PATTERN, "Not a valid hostname"),
    count: z.number().int().min(1).max(MAX_SERVERS_PER_CREATE).default(1),
    /** Catalogue codes: region (`fsn1`), server type (`cx23`), image (`ubuntu-26.04`). */
    region: z.string().min(1).max(64),
    serverType: z.string().min(1).max(64),
    image: z.string().min(1).max(128).optional(),
    /** Our id of a snapshot or backup to start from, instead of a catalogue image. */
    imageId: z.uuid().optional(),
    /** Our ids of the project's SSH keys; none means the provider generates a root password. */
    sshKeyIds: z.array(z.uuid()).max(50).default([]),
    /**
     * Our ids of private networks to attach the server to; each needs a subnet
     * in the region's network zone. With one, the server may go without
     * public addresses.
     */
    networkIds: z.array(z.uuid()).max(MAX_NETWORKS_PER_SERVER).default([]),
    /** Our ids of firewalls applied from the start; none means every port is open. */
    firewallIds: z.array(z.uuid()).max(MAX_FIREWALLS_PER_SERVER).default([]),
    publicIpv4: z.boolean().default(true),
    publicIpv6: z.boolean().default(true),
    backups: z.boolean().default(false),
    /** Our ids of free volumes in the region to attach from the start (one server only). */
    volumeIds: z.array(z.uuid()).max(MAX_VOLUMES_PER_SERVER).optional(),
    /** Mount the attached volumes that have a file system. */
    automount: z.boolean().optional(),
    /** Our id of a spread placement group to create the servers in. */
    placementGroupId: z.uuid().optional(),
    labels: Labels.default({}),
    /**
     * Install the Autopilot agent on first boot (docs/autopilot.md): our API
     * enrols each server with the hub and adds the install to its cloud-init.
     */
    autopilot: AutopilotOptions.optional(),
    userData: z
      .string()
      .refine((v) => byteLength(v) <= MAX_USER_DATA_BYTES, "User data is larger than 32 KiB")
      .optional(),
    /**
     * Make the server trust the organisation's SSH CA at first boot (a
     * cloud-init part next to `userData`), so the browser terminal signs in
     * with one-time certificates. The panel turns it on by default.
     */
    browserTerminal: z.boolean().default(false),
  })
  .refine((v) => (v.image === undefined) !== (v.imageId === undefined), {
    message: "Give either a catalogue image or a snapshot or backup",
    path: ["image"],
  })
  .refine((v) => (v.volumeIds ?? []).length === 0 || v.count === 1, {
    message: "A volume attaches to one server: create one server to attach volumes",
    path: ["volumeIds"],
  })
  .refine((v) => serverNames(v.name, v.count).every((n) => n.length <= 253), {
    message: "Names with the -<n> suffix are longer than 253 characters",
    path: ["name"],
  });
export type CreateServerRequest = z.infer<typeof CreateServerRequest>;

/**
 * A root password the provider generated because no SSH key was chosen.
 * Returned once in the create response; CloudServer never stores or logs it.
 */
export const RootPassword = z.object({ serverId: z.uuid(), password: z.string() });
export type RootPassword = z.infer<typeof RootPassword>;

export const CreateServerResponse = z.object({
  servers: z.array(Server),
  /** One per server: creating it, followed until the provider has started it. */
  operations: z.array(Operation),
  rootPasswords: z.array(RootPassword),
  /**
   * Servers of a multi-server request the provider refused after others were
   * already created (e.g. a limit reached); empty when all were created.
   */
  failed: z.array(z.object({ name: z.string(), code: ErrorCode, reason: z.string().optional() })),
});
export type CreateServerResponse = z.infer<typeof CreateServerResponse>;
